/**
 * Snapshot Analyzer for Navigation Crawling
 *
 * Parses Playwright accessibility snapshots (YAML format) to:
 * 1. Extract all link URLs for tracking
 * 2. Detect modal/overlay presence
 * 3. Find close buttons for modal dismissal
 * 4. Generate contextual hints for the agent
 */

import { consoleLogger, type BrowserLogger } from './logger.js';

let logger: BrowserLogger = consoleLogger;

/**
 * Set the logger used by the snapshot analyzer.
 * Call this at startup to inject a custom logger (e.g., pino).
 */
export function setSnapshotAnalyzerLogger(l: BrowserLogger): void {
  logger = l;
}

/**
 * Single source-of-truth tuple for substrate-v2 provenance values. Both the
 * exported {@link SnapshotElementSource} type and the runtime validation set
 * are derived from this so they cannot drift.
 */
const SNAPSHOT_ELEMENT_SOURCES_TUPLE = [
  'aria',
  'augmented:label-for',
  'augmented:sibling-text',
  'augmented:dom-mutation',
  'synthesized:css-hint',
] as const;

/**
 * Provenance for a substrate-v2 field. Tracks whether a value came from
 * the native ARIA tree, was injected by an augmentation pass, or was
 * synthesized from a CSS pattern match. Read by `buildLocator` to skip
 * predicates that depend on low-durability data (e.g. live DOM mutations).
 */
export type SnapshotElementSource = (typeof SNAPSHOT_ELEMENT_SOURCES_TUPLE)[number];

export interface SnapshotElementCssHints {
  /** Element tag from `~css=tag.cls.cls` parsing (e.g. `abbr`). */
  tag?: string;
  id?: string;
  classes?: string[];
  testIds?: string[];
  automationId?: string;
  /** `data-id` entity attribute (e.g. a grid row's `__new__…` / ULID id).
   * Used as an ancestor scope anchor after volatile-suffix stripping. */
  dataId?: string;
}

export interface SnapshotElementBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface SnapshotElement {
  role: string;
  ref: string;
  text?: string;
  url?: string;
  level?: number;
  inputValue?: string; // For textbox/combobox, the current input value after the colon
  children: SnapshotElement[];
  attributes: Record<string, string>;
  rawLine: string;

  // Substrate-v2 fields (all optional, additive over v1).
  // Populated only when the snapshot YAML carries `~`-prefixed synthetic
  // attributes; v1 snapshots leave them unset.

  /** Provenance for `text` (accessible name). */
  nameSource?: SnapshotElementSource;
  /** Provenance for entries in `attributes`. Keys mirror `attributes`. */
  attributeSources?: Record<string, SnapshotElementSource>;
  /** Index among same-role siblings under the parent (0-based). */
  siblingRoleIndex?: number;
  /** Position in the rendered DOM order, distinct from tree order. */
  domOrderIndex?: number;
  /** Closest containing iframe's CSS path, captured at snapshot time. */
  frameSelector?: string;
  /** Stable-looking attrs surfaced from the live DOM (filtered). */
  cssHints?: SnapshotElementCssHints;
  /**
   * DOM-grounded icon class on a child SVG (e.g. `lucide-sliders-horizontal`).
   * Set by the augmenter from a `<svg class="lucide-X">` inside an unlabeled
   * icon control. The renderer translates this to a friendly name; locator
   * predicates use it as a CSS anchor (`role:has(svg.lucide-X)`).
   */
  iconClass?: string;
  /**
   * DOM-grounded semantic class token on the element itself (e.g.
   * `delete-btn`, `IconButton-filter`). Set by the augmenter from
   * `extractSemanticHint(className)` when a meaningful class survived the
   * noise filter. Locator predicates use it as a CSS anchor (`role.<class>`).
   */
  semanticClass?: string;
  /**
   * DOM-grounded `data-field` token on a grid cell (e.g. a MUI DataGrid
   * editable cell's `house_rate::GroupA`). Set by the per-ref augmenter.
   * Used by the formatter to surface otherwise-empty editable cells and by
   * `buildLocator` as a row-scoped, semantic (non-ordinal) locator anchor.
   */
  dataField?: string;
  /**
   * True when the augmenter classified a grid cell as editable (MUI
   * `--editable` class or an editable control inside). Lets the formatter
   * render empty editable cells so the agent can target them by ref.
   */
  isEditable?: boolean;
  /** Visibility classification at capture time. */
  visibility?: 'visible' | 'off-screen' | 'hidden';
  /**
   * True when a `role=dialog`/`alertdialog` was verified (against the live DOM
   * during augmentation) to be not actually rendered — collapsed, zero-box, or
   * fully off-screen. The formatter must not treat such a dialog as a blocking
   * modal (even with `aria-modal=true`) nor list it in the DIALOG STACK.
   */
  dialogInert?: boolean;
  /** Bounding box at capture time (for visibility/proximity reasoning). */
  boundingBox?: SnapshotElementBoundingBox;
  /** `2` when this node carried the `~v2` marker. Capture writes it on the root. */
  substrateVersion?: 2;
  /**
   * Substrate-v2 augmented role override. Set when capture detected a
   * non-ARIA role for an element that's already in the a11y tree (e.g. a
   * modal `<div>` that v1 would have mutated to `role=dialog`). The native
   * `role` field stays `generic`; downstream consumers that dispatch on
   * role (the formatter's dialog detection, focus-scope, FOCUSED markers)
   * read through `effectiveRole(el)` which prefers `augmentedRole` when
   * `attributeSources.role === 'augmented:dom-mutation'`.
   */
  augmentedRole?: string;
  /**
   * Substrate-v2 augmented name override. Same shape as `augmentedRole`
   * for elements whose name needs to come from a non-ARIA source (e.g.
   * derived from a modal's heading text).
   */
  augmentedName?: string;
}

export interface ModalInfo {
  detected: boolean;
  refs: string[];
  type: 'dialog' | 'alertdialog' | 'overlay' | 'unknown';
  closeButtonRef?: string;
  dismissalHints: string[];
}

export interface SnapshotAnalysis {
  links: string[];
  modalInfo: ModalInfo | null;
  hints: string[];
  elementCount: number;
}

export interface ParseResult {
  elements: SnapshotElement[];
  skippedLines: number;
  totalElementLines: number;
}

/**
 * Extract the YAML snapshot from an MCP response.
 * The snapshot is typically inside a ```yaml code block after "Page Snapshot:"
 */
export function extractSnapshotYaml(mcpResponse: string | unknown): string {
  // Handle MCPContentResult objects (e.g., from snapshot in 'both' mode)
  const text =
    typeof mcpResponse === 'string'
      ? mcpResponse
      : mcpResponse !== null &&
          mcpResponse !== undefined &&
          typeof mcpResponse === 'object' &&
          'text' in mcpResponse &&
          typeof (mcpResponse as { text: unknown }).text === 'string'
        ? (mcpResponse as { text: string }).text
        : String(mcpResponse);

  // Look for yaml code block after Page Snapshot:
  const yamlMatch = text.match(/Page Snapshot:\s*```yaml\s*([\s\S]*?)```/);
  if (yamlMatch) {
    return yamlMatch[1].trim();
  }

  // Fallback: if the response is just the snapshot (no wrapper)
  if (text.includes('[ref=')) {
    return text.trim();
  }

  return '';
}

/**
 * Parse the YAML-like snapshot format into a tree structure.
 * Format example:
 * - generic [ref=e2]:
 *   - heading "Title" [level=1] [ref=e3]
 *   - link "Click here" [ref=e4]:
 *     - /url: https://example.com
 */
export function parseSnapshot(yaml: string): SnapshotElement[] {
  return parseSnapshotWithMetrics(yaml).elements;
}

/**
 * Parse snapshot YAML with completeness metrics.
 * Returns the parsed elements along with counts of skipped/unparseable lines.
 * Use this when you need to track parse quality (e.g. in browser tools).
 */
export function parseSnapshotWithMetrics(yaml: string): ParseResult {
  if (!yaml || yaml.trim() === '') {
    return { elements: [], skippedLines: 0, totalElementLines: 0 };
  }

  const lines = yaml.split('\n');
  const root: SnapshotElement[] = [];
  const stack: { indent: number; element: SnapshotElement }[] = [];

  // Track the last element we added for metadata attachment
  let lastElement: SnapshotElement | null = null;
  let skippedLines = 0;
  const skippedContent: string[] = [];
  let totalElementLines = 0;

  for (const rawLine of lines) {
    if (!rawLine.trim()) continue;

    // Calculate indent level (2 spaces per level)
    const leadingSpaces = rawLine.match(/^(\s*)/)?.[1]?.length ?? 0;
    const indent = Math.floor(leadingSpaces / 2);
    const content = rawLine.trim();

    // Handle metadata lines like "- /url:" - these belong to the previous element
    // They start with "- /" which indicates metadata
    if (content.startsWith('- /')) {
      // Add URL to the last element at a shallower indent level
      // Find the element that this metadata belongs to (should be at indent - 1)
      const metadataContent = content.substring(2); // Remove "- "
      const targetIndent = indent - 1;

      // Find the element at the target indent
      let targetElement: SnapshotElement | null = null;
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].indent === targetIndent) {
          targetElement = stack[i].element;
          break;
        }
      }

      // Fallback to last element
      if (!targetElement) {
        targetElement = lastElement;
      }

      if (targetElement) {
        if (metadataContent.startsWith('/url:')) {
          targetElement.url = metadataContent.replace('/url:', '').trim();
        } else if (metadataContent.startsWith('/')) {
          const colonIdx = metadataContent.indexOf(':');
          if (colonIdx > 1) {
            const key = metadataContent.substring(1, colonIdx).trim();
            const value = metadataContent.substring(colonIdx + 1).trim();
            if (key && value) {
              targetElement.attributes[key] = value;
            }
          }
        }
      }
      continue;
    }

    // Parse element line: "- role "text" [attr=val] [ref=eN]:"
    const element = parseElementLine(content, rawLine);
    if (!element) {
      // Only count lines that look like element definitions (start with "- ")
      // but couldn't be parsed. Skip non-element lines silently.
      if (content.startsWith('- ')) {
        totalElementLines++;
        skippedLines++;
        skippedContent.push(content);
      }
      continue;
    }

    totalElementLines++;

    // Pop stack until we find the right parent level
    while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    // Add to parent or root
    if (stack.length > 0) {
      stack[stack.length - 1].element.children.push(element);
    } else {
      root.push(element);
    }

    // Push to stack for potential children
    stack.push({ indent, element });
    lastElement = element;
  }

  if (skippedLines > 0) {
    logger.warn('Snapshot parse incomplete', {
      skippedLines,
      skippedContent,
      parsedElements: totalElementLines - skippedLines,
      totalElementLines,
    });
  }

  return { elements: root, skippedLines, totalElementLines };
}

/**
 * Parse a single element line from the snapshot.
 * Examples:
 * - "- generic [ref=e2]:"
 * - "- heading \"Title\" [level=1] [ref=e3]"
 * - "- link \"Click here\" [ref=e4]:"
 * - "- button \"Submit\" [ref=e5]"
 * - "- textbox \"Search\" [ref=e6]: user input value"
 */
function parseElementLine(content: string, rawLine: string): SnapshotElement | null {
  // Must start with "- "
  if (!content.startsWith('- ')) return null;

  let line = content.substring(2); // Remove "- "

  // Strip YAML single-quote wrapping. When element text contains colons
  // (e.g. "Rows per page: 25"), YAML serializes the element key as:
  //   'combobox "Rows per page: 25" [ref=e542]': "25"
  // Unwrap only the quoted key and preserve any trailing input value.
  if (line.startsWith("'")) {
    const quotedKeyEnd = line.indexOf("':");
    if (quotedKeyEnd !== -1) {
      line = line.slice(1, quotedKeyEnd) + line.slice(quotedKeyEnd + 1);
    } else if (line.endsWith("'")) {
      line = line.slice(1, -1);
    }
  }

  // Extract role (first word before space, quote, or bracket)
  const roleMatch = line.match(/^(\w+)/);
  if (!roleMatch) return null;
  const role = roleMatch[1];

  // Extract text content (quoted string)
  const textMatch = line.match(/"([^"]*)"/);
  let text = textMatch ? textMatch[1] : undefined;
  if (!text && role === 'text') {
    const textValue = line
      .replace(/^text:\s*/, '')
      .replace(/\s*\[~?[\w:-]+(?:=[^\]]+)?\]/g, '')
      .trim();
    text = textValue || undefined;
  }

  // Extract ref
  const refMatch = line.match(/\[ref=(\w+)\]/);
  const ref = refMatch ? refMatch[1] : '';

  // Extract other attributes like [level=1], [aria-modal=true].
  // Also captures `~`-prefixed substrate-v2 synthetic attrs (e.g. `~name-src=aria`,
  // `~css=abbr.search-choice-close`, `~attr-src:aria-label=...`) — those are
  // routed below into typed fields and removed from `attributes` so callers
  // don't see them as ARIA attrs. The `:` in the key is allowed for v2's
  // `~attr-src:<attrName>` provenance markers.
  const attributes: Record<string, string> = {};
  const attrMatches = line.matchAll(/\[(~?[\w:-]+)=([^\]]+)\]/g);
  for (const match of attrMatches) {
    if (match[1] !== 'ref') {
      attributes[match[1]] = match[2];
    }
  }

  // Extract boolean attributes like [active], [disabled], [~v2] (no value).
  const booleanAttrMatches = line.matchAll(/\[(~?[\w:-]+)\](?![^[]*=)/g);
  for (const match of booleanAttrMatches) {
    const attrName = match[1];
    // Skip 'ref' and already captured attributes
    if (attrName !== 'ref' && !attributes[attrName]) {
      attributes[attrName] = 'true';
    }
  }

  // Pull v2 synthetic attrs out of `attributes` and into typed fields.
  const v2 = extractAndStripV2Fields(attributes);

  // Extract level for headings
  const level = attributes['level'] ? parseInt(attributes['level'], 10) : undefined;

  // Extract trailing value after `]:`. For form controls this is the current
  // input value. Some Playwright text/heading nodes serialize their text this
  // way instead of as a quoted accessible name.
  let inputValue: string | undefined;
  const trailingValueMatch = line.match(/\]:\s*(.+)$/);
  if (trailingValueMatch && trailingValueMatch[1].trim()) {
    const trailingValue = trailingValueMatch[1].trim();
    inputValue = trailingValue;
    if (!text && (role === 'heading' || role === 'text')) {
      text = trailingValue;
    }
  }

  return {
    role,
    ref,
    text,
    level,
    inputValue,
    children: [],
    attributes,
    rawLine,
    ...v2,
  };
}

/**
 * Extract substrate-v2 synthetic attributes from a parsed `attributes` bag.
 * Mutates `attributes` to remove the `~`-prefixed keys so downstream consumers
 * see only native ARIA attributes. Returns the typed v2 fields to spread onto
 * the `SnapshotElement`.
 *
 * Renamed from `extractV2Fields` to make the strip behavior explicit at the
 * callsite — passing a shared bag would have surprising side effects.
 */
function extractAndStripV2Fields(
  attributes: Record<string, string>
): Partial<SnapshotElement> {
  const out: Partial<SnapshotElement> = {};
  const cssHints: SnapshotElementCssHints = {};
  const attributeSources: Record<string, SnapshotElementSource> = {};
  let sawCssHint = false;
  let sawAttrSource = false;

  for (const key of Object.keys(attributes)) {
    if (!key.startsWith('~')) continue;
    // Synthetic v2 values percent-encode `[`, `]`, `,` so they survive the
    // bracket-delimited attribute syntax. Decode here.
    const value = decodeV2Value(attributes[key]);
    delete attributes[key];

    switch (key) {
      case '~name-src':
        if (isSnapshotElementSource(value)) out.nameSource = value;
        break;
      case '~aug-role':
        // Capture-time role override for elements already in the a11y tree
        // (e.g. modal `<div>` that v1 would have mutated to `role=dialog`).
        // Downstream consumers should read through `effectiveRole(el)` to
        // pick the augmented role over the native one when source is
        // augmented.
        out.augmentedRole = value;
        break;
      case '~aug-name':
        // Capture-time name override paired with `~aug-role` for elements
        // whose accessible name needs to come from a non-ARIA source.
        out.augmentedName = value;
        break;
      case '~role-src':
        if (isSnapshotElementSource(value)) {
          attributeSources['role'] = value;
          sawAttrSource = true;
        }
        break;
      case '~sibling-idx': {
        const n = parseInt(value, 10);
        if (Number.isFinite(n)) out.siblingRoleIndex = n;
        break;
      }
      case '~dom-order': {
        const n = parseInt(value, 10);
        if (Number.isFinite(n)) out.domOrderIndex = n;
        break;
      }
      case '~frame':
        out.frameSelector = value;
        break;
      case '~visibility':
        if (value === 'visible' || value === 'off-screen' || value === 'hidden') {
          out.visibility = value;
        }
        break;
      case '~dialog-inert':
        if (value === 'true') out.dialogInert = true;
        break;
      case '~bbox': {
        // Format: `x:y:w:h` (numbers). Colon avoids conflict with the
        // bracket-delimited attr syntax and needs no encoding.
        const parts = value.split(':').map((s) => parseFloat(s));
        if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
          out.boundingBox = { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
        }
        break;
      }
      case '~css': {
        // Format: `tag.cls.cls` (no leading dot). Tag is preserved on
        // `cssHints.tag` so the locator algorithm can fall back to a full
        // tag-qualified CSS selector when needed.
        const parts = value.split('.');
        if (parts[0]) cssHints.tag = parts[0];
        cssHints.classes = cssHints.classes ?? [];
        for (const cls of parts.slice(1)) {
          if (cls) cssHints.classes.push(cls);
        }
        sawCssHint = true;
        break;
      }
      case '~css-id':
        cssHints.id = value;
        sawCssHint = true;
        break;
      case '~css-class':
        cssHints.classes = cssHints.classes ?? [];
        for (const cls of value.split(' ')) {
          if (cls) cssHints.classes.push(cls);
        }
        sawCssHint = true;
        break;
      case '~test-id':
        cssHints.testIds = cssHints.testIds ?? [];
        cssHints.testIds.push(value);
        sawCssHint = true;
        break;
      case '~data-id':
        cssHints.dataId = value;
        sawCssHint = true;
        break;
      case '~automation-id':
        cssHints.automationId = value;
        sawCssHint = true;
        break;
      case '~icon-class':
        out.iconClass = value;
        break;
      case '~semantic-class':
        out.semanticClass = value;
        break;
      case '~field':
        out.dataField = value;
        break;
      case '~editable':
        out.isEditable = value === 'true';
        break;
      case '~v2':
        out.substrateVersion = 2;
        break;
      default:
        // `~attr-src:<attrName>=<source>` — provenance for a specific ARIA attr.
        if (key.startsWith('~attr-src:')) {
          const attrName = key.slice('~attr-src:'.length);
          if (attrName && isSnapshotElementSource(value)) {
            attributeSources[attrName] = value;
            sawAttrSource = true;
          }
        }
        break;
    }
  }

  if (sawCssHint) out.cssHints = cssHints;
  if (sawAttrSource) out.attributeSources = attributeSources;
  return out;
}

/**
 * Decode percent-escaped chars used in substrate-v2 synthetic attr values.
 * v2 capture encodes `%`, `[`, `]`, ` `, `,`, `=` so values like
 * `iframe[name="card-number"]` and CSS selectors with spaces or commas
 * survive the bracket-delimited attr syntax. Use the matching
 * {@link encodeV2Value} when emitting.
 *
 * Decode order matters: `%` decodes last so `%2520` round-trips to `%20`.
 */
export function decodeV2Value(value: string): string {
  return value
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']')
    .replace(/%20/gi, ' ')
    .replace(/%2C/gi, ',')
    .replace(/%3D/gi, '=')
    .replace(/%25/gi, '%');
}

/**
 * Inverse of {@link decodeV2Value}; for use by snapshot-augmentation v2
 * emitters. Encodes `%` first so other escapes don't double-encode.
 */
export function encodeV2Value(value: string): string {
  return value
    .replace(/%/g, '%25')
    .replace(/\[/g, '%5B')
    .replace(/\]/g, '%5D')
    .replace(/ /g, '%20')
    .replace(/,/g, '%2C')
    .replace(/=/g, '%3D');
}

const SNAPSHOT_ELEMENT_SOURCES: ReadonlySet<SnapshotElementSource> = new Set(
  SNAPSHOT_ELEMENT_SOURCES_TUPLE
);

function isSnapshotElementSource(value: string): value is SnapshotElementSource {
  return (SNAPSHOT_ELEMENT_SOURCES as ReadonlySet<string>).has(value);
}

/**
 * Effective role for a substrate-v2 element. When the element carries an
 * `augmentedRole` and its `attributeSources.role` is augmented, the
 * augmented role wins. Otherwise, the native ARIA `role` is returned.
 *
 * Used by the formatter and any consumer that dispatches on role —
 * dialog detection, focus scope, FOCUSED markers, etc. — so substrate-v2
 * captures can reproduce v1's pre-snapshot DOM mutation
 * (`setAttribute('role', 'dialog')`) without actually mutating.
 *
 * v1 elements always return their native `role` because they never
 * carry `augmentedRole`.
 */
export function effectiveRole(el: SnapshotElement): string {
  if (
    el.augmentedRole &&
    el.attributeSources?.['role'] === 'augmented:dom-mutation'
  ) {
    return el.augmentedRole;
  }
  return el.role;
}

/**
 * Effective accessible name. Same rule as {@link effectiveRole}: when an
 * `augmentedName` is set and `attributeSources.role === 'augmented:dom-mutation'`
 * (i.e. capture set the override at the same site as the role override),
 * the augmented name wins; otherwise the native `text` is returned.
 *
 * The role-source key is reused intentionally — modal/dialog-style
 * augmentations that override role typically also override the accessible
 * name as a paired decision.
 */
export function effectiveName(el: SnapshotElement): string | undefined {
  if (
    el.augmentedName !== undefined &&
    el.attributeSources?.['role'] === 'augmented:dom-mutation'
  ) {
    return el.augmentedName;
  }
  return el.text;
}

/**
 * Detect whether a parsed snapshot was captured under the substrate-v2
 * pipeline. Looks for the `~v2` marker on any node (capture writes it on
 * the root, but a per-node check is robust to YAML reordering).
 */
export function isSubstrateV2(elements: SnapshotElement[]): boolean {
  for (const el of elements) {
    if (el.substrateVersion === 2) return true;
    if (
      el.nameSource !== undefined ||
      el.siblingRoleIndex !== undefined ||
      el.cssHints !== undefined ||
      el.frameSelector !== undefined
    ) {
      return true;
    }
    if (isSubstrateV2(el.children)) return true;
  }
  return false;
}

/**
 * Extract all link URLs from a parsed snapshot tree.
 */
export function extractLinksFromTree(elements: SnapshotElement[]): string[] {
  const links: string[] = [];

  function traverse(element: SnapshotElement) {
    if (element.url && element.url.startsWith('http')) {
      links.push(element.url);
    }
    for (const child of element.children) {
      traverse(child);
    }
  }

  for (const element of elements) {
    traverse(element);
  }

  return [...new Set(links)]; // Deduplicate
}

/**
 * Extract all link URLs directly from YAML string using regex.
 * Faster than full parsing when only links are needed.
 */
export function extractLinks(yaml: string): string[] {
  if (!yaml) return [];

  const links: string[] = [];

  // Pattern 1: /url: https://...
  const urlPattern = /\/url:\s*(https?:\/\/[^\s\n]+)/gi;
  let match;
  while ((match = urlPattern.exec(yaml)) !== null) {
    links.push(match[1]);
  }

  // Pattern 2: href or url in attributes
  const hrefPattern = /\[(?:href|url)=([^\]]+)\]/gi;
  while ((match = hrefPattern.exec(yaml)) !== null) {
    if (match[1].startsWith('http')) {
      links.push(match[1]);
    }
  }

  return [...new Set(links)];
}

/**
 * Detect if a modal or overlay is present in the snapshot.
 */
export function detectModal(elements: SnapshotElement[]): ModalInfo | null {
  const modalRefs: string[] = [];
  let modalType: ModalInfo['type'] = 'unknown';
  let closeButtonRef: string | undefined;

  function traverse(element: SnapshotElement) {
    const role = element.role.toLowerCase();

    // Check for dialog/alertdialog roles
    if (role === 'dialog' || role === 'alertdialog') {
      modalRefs.push(element.ref);
      modalType = role as 'dialog' | 'alertdialog';
    }

    // Check for aria-modal attribute
    if (element.attributes['aria-modal'] === 'true') {
      if (!modalRefs.includes(element.ref)) {
        modalRefs.push(element.ref);
      }
      modalType = 'dialog';
    }

    // Look for close buttons within modals
    if (modalRefs.length > 0 && !closeButtonRef) {
      if (role === 'button') {
        const text = (element.text || '').toLowerCase();
        const ariaLabel = (element.attributes['aria-label'] || '').toLowerCase();
        if (
          text === 'close' ||
          text === 'x' ||
          text === 'dismiss' ||
          text === 'cancel' ||
          ariaLabel.includes('close') ||
          ariaLabel.includes('dismiss')
        ) {
          closeButtonRef = element.ref;
        }
      }
    }

    for (const child of element.children) {
      traverse(child);
    }
  }

  for (const element of elements) {
    traverse(element);
  }

  if (modalRefs.length === 0) {
    return null;
  }

  // Generate dismissal hints
  const dismissalHints: string[] = ['Press Escape key'];
  if (closeButtonRef) {
    dismissalHints.unshift(`Click close button (ref=${closeButtonRef})`);
  }
  dismissalHints.push('Click outside the modal area');

  return {
    detected: true,
    refs: modalRefs,
    type: modalType,
    closeButtonRef,
    dismissalHints,
  };
}

/**
 * Detect modal from YAML string directly without full parsing.
 * Faster for quick checks.
 */
export function detectModalFromYaml(yaml: string): ModalInfo | null {
  if (!yaml) return null;

  const hasDialog = /\bdialog\b/i.test(yaml);
  const hasAlertDialog = /\balertdialog\b/i.test(yaml);
  const hasAriaModal = /\[aria-modal=true\]/i.test(yaml);

  if (!hasDialog && !hasAlertDialog && !hasAriaModal) {
    return null;
  }

  // Do full parse to get details
  const elements = parseSnapshot(yaml);
  return detectModal(elements);
}

/**
 * Find a close button reference in the snapshot.
 * Returns the ref of the most likely close button, or undefined.
 */
export function findCloseButton(yaml: string): string | undefined {
  const elements = parseSnapshot(yaml);

  function traverse(element: SnapshotElement): string | undefined {
    if (element.role.toLowerCase() === 'button') {
      const text = (element.text || '').toLowerCase();
      const ariaLabel = (element.attributes['aria-label'] || '').toLowerCase();

      // Priority: exact "close" or "x" text
      if (text === 'close' || text === 'x' || text === '×') {
        return element.ref;
      }

      // Secondary: aria-label containing close
      if (ariaLabel.includes('close') || ariaLabel.includes('dismiss')) {
        return element.ref;
      }
    }

    for (const child of element.children) {
      const found = traverse(child);
      if (found) return found;
    }

    return undefined;
  }

  for (const element of elements) {
    const found = traverse(element);
    if (found) return found;
  }

  return undefined;
}

/**
 * Generate contextual hints based on snapshot analysis.
 */
function generateHints(
  elementCount: number,
  modalInfo: ModalInfo | null,
  links: string[]
): string[] {
  const hints: string[] = [];

  // Modal hint
  if (modalInfo?.detected) {
    hints.push(
      `Modal/overlay detected (type: ${modalInfo.type}). ` +
        'Use browser_dismiss_overlay tool or try: ' +
        modalInfo.dismissalHints.slice(0, 2).join(', ')
    );
  }

  // Low link count warning - page has many elements but few links
  if (elementCount > 10 && links.length < 2) {
    hints.push(
      'Page has many elements but few links detected. ' +
        'Some links may be hidden behind expandable menus or modals.'
    );
  }

  return hints;
}

/**
 * Perform full analysis of a snapshot.
 * Returns links, modal info, and contextual hints.
 */
export function analyzeSnapshot(yaml: string): SnapshotAnalysis {
  if (!yaml || yaml.trim() === '') {
    return {
      links: [],
      modalInfo: null,
      hints: [],
      elementCount: 0,
    };
  }

  const elements = parseSnapshot(yaml);
  const links = extractLinksFromTree(elements);
  const modalInfo = detectModal(elements);

  // Count total elements (including nested)
  let elementCount = 0;
  function countElements(el: SnapshotElement) {
    elementCount++;
    el.children.forEach(countElements);
  }
  elements.forEach(countElements);

  // Generate hints based on analysis
  const hints = generateHints(elementCount, modalInfo, links);

  return {
    links,
    modalInfo,
    hints,
    elementCount,
  };
}

/**
 * Append hints to an MCP response string.
 */
export function appendHintsToResponse(response: string, hints: string[]): string {
  if (hints.length === 0) return response;

  const hintBlock = `\n\n[SYSTEM HINTS]\n${hints.map((h) => `- ${h}`).join('\n')}`;
  return response + hintBlock;
}
